import { produce } from 'immer';
import type {
  OffersSummaryResponse,
  OfferKpiComparisonResponse,
  TopOfferItem,
  OfferRedemptionTrendResponse,
  OfferDiscountBreakdownResponse,
  OfferDiscountByTypeResponse,
  OfferDetailResponse,
} from '../../../interface/offersReportInterface';
import {
  FETCH_OFFERS_SUMMARY_REQUEST,
  FETCH_OFFERS_SUMMARY_SUCCESS,
  FETCH_OFFERS_SUMMARY_FAILURE,
  FETCH_OFFER_KPI_COMPARISON_REQUEST,
  FETCH_OFFER_KPI_COMPARISON_SUCCESS,
  FETCH_OFFER_KPI_COMPARISON_FAILURE,
  FETCH_TOP_OFFERS_REQUEST,
  FETCH_TOP_OFFERS_SUCCESS,
  FETCH_TOP_OFFERS_FAILURE,
  FETCH_OFFERS_KPI_REQUEST,
  FETCH_OFFERS_KPI_SUCCESS,
  FETCH_OFFERS_KPI_FAILURE,
  FETCH_OFFER_REDEMPTION_TREND_REQUEST,
  FETCH_OFFER_REDEMPTION_TREND_SUCCESS,
  FETCH_OFFER_REDEMPTION_TREND_FAILURE,
  FETCH_OFFER_DISCOUNT_BREAKDOWN_REQUEST,
  FETCH_OFFER_DISCOUNT_BREAKDOWN_SUCCESS,
  FETCH_OFFER_DISCOUNT_BREAKDOWN_FAILURE,
  FETCH_OFFER_DISCOUNT_BY_TYPE_REQUEST,
  FETCH_OFFER_DISCOUNT_BY_TYPE_SUCCESS,
  FETCH_OFFER_DISCOUNT_BY_TYPE_FAILURE,
  FETCH_OFFER_DETAIL_REQUEST,
  FETCH_OFFER_DETAIL_SUCCESS,
  FETCH_OFFER_DETAIL_FAILURE,
  CLEAR_OFFER_DETAIL,
} from './offersReportConstants';

const initialState: {
  // Summary KPIs
  offersSummaryLoading: boolean;
  offersSummary: OffersSummaryResponse | null;
  offersSummaryFailure: boolean;
  // KPI Comparison (top-row deltas + AOV value/delta)
  kpiComparisonLoading: boolean;
  kpiComparison: OfferKpiComparisonResponse | null;
  kpiComparisonFailure: boolean;
  // Top Offers (Top Offers table)
  topOffersLoading: boolean;
  topOffers: TopOfferItem[];
  topOffersTotalElements: number;
  topOffersTotalPages: number;
  // Active-offer count returned alongside the FILTERED table page. The KPI tile
  // does NOT read this (see offersKpi* below) — it moves with search/status.
  topOffersActiveOffers: number;
  topOffersFailure: boolean;
  // Offers KPI row (Active Offers + Top Offer tiles) — fed by the unfiltered
  // top-offers call, so table search/status/sort/paging never disturbs it.
  offersKpiLoading: boolean;
  offersKpiTopOffer: TopOfferItem | null;
  offersKpiActiveOffers: number;
  offersKpiLoaded: boolean;
  offersKpiFailure: boolean;
  // Redemption Trend drill
  redemptionTrendLoading: boolean;
  redemptionTrend: OfferRedemptionTrendResponse | null;
  redemptionTrendFailure: boolean;
  // Discount Breakdown drill
  discountBreakdownLoading: boolean;
  discountBreakdown: OfferDiscountBreakdownResponse | null;
  discountBreakdownFailure: boolean;
  // Discount by Type donut
  discountByTypeLoading: boolean;
  discountByType: OfferDiscountByTypeResponse | null;
  discountByTypeFailure: boolean;
  // Individual Offer drill
  offerDetailLoading: boolean;
  offerDetail: OfferDetailResponse | null;
  offerDetailFailure: boolean;
} = {
  offersSummaryLoading: false,
  offersSummary: null,
  offersSummaryFailure: false,

  kpiComparisonLoading: false,
  kpiComparison: null,
  kpiComparisonFailure: false,

  topOffersLoading: false,
  topOffers: [],
  topOffersTotalElements: 0,
  topOffersTotalPages: 1,
  topOffersActiveOffers: 0,
  topOffersFailure: false,

  offersKpiLoading: false,
  offersKpiTopOffer: null,
  offersKpiActiveOffers: 0,
  offersKpiLoaded: false,
  offersKpiFailure: false,

  redemptionTrendLoading: false,
  redemptionTrend: null,
  redemptionTrendFailure: false,

  discountBreakdownLoading: false,
  discountBreakdown: null,
  discountBreakdownFailure: false,

  discountByTypeLoading: false,
  discountByType: null,
  discountByTypeFailure: false,

  offerDetailLoading: false,
  offerDetail: null,
  offerDetailFailure: false,
};

export default function offersReportReducer(state = initialState, action: any) {
  return produce(state, (draft) => {
    switch (action.type) {
      case FETCH_OFFERS_SUMMARY_REQUEST:
        draft.offersSummaryLoading = true;
        draft.offersSummary = null;
        draft.offersSummaryFailure = false;
        break;
      case FETCH_OFFERS_SUMMARY_SUCCESS:
        draft.offersSummaryLoading = false;
        draft.offersSummary = action.payload;
        draft.offersSummaryFailure = false;
        break;
      case FETCH_OFFERS_SUMMARY_FAILURE:
        draft.offersSummaryLoading = false;
        draft.offersSummary = null;
        draft.offersSummaryFailure = true;
        break;

      case FETCH_OFFER_KPI_COMPARISON_REQUEST:
        draft.kpiComparisonLoading = true;
        draft.kpiComparison = null;
        draft.kpiComparisonFailure = false;
        break;
      case FETCH_OFFER_KPI_COMPARISON_SUCCESS:
        draft.kpiComparisonLoading = false;
        draft.kpiComparison = action.payload;
        draft.kpiComparisonFailure = false;
        break;
      case FETCH_OFFER_KPI_COMPARISON_FAILURE:
        draft.kpiComparisonLoading = false;
        draft.kpiComparison = null;
        draft.kpiComparisonFailure = true;
        break;

      case FETCH_TOP_OFFERS_REQUEST:
        draft.topOffersLoading = true;
        draft.topOffers = [];
        draft.topOffersFailure = false;
        break;
      case FETCH_TOP_OFFERS_SUCCESS:
        draft.topOffersLoading = false;
        draft.topOffers = action.payload.content ?? [];
        draft.topOffersTotalElements = Number(action.payload.totalElements) || 0;
        draft.topOffersTotalPages = Number(action.payload.totalPages) || 1;
        draft.topOffersActiveOffers = Number(action.payload.activeOffers) || 0;
        draft.topOffersFailure = false;
        break;
      case FETCH_TOP_OFFERS_FAILURE:
        draft.topOffersLoading = false;
        draft.topOffers = [];
        draft.topOffersTotalElements = 0;
        draft.topOffersTotalPages = 1;
        draft.topOffersActiveOffers = 0;
        draft.topOffersFailure = true;
        break;

      // Offers KPI row — unfiltered top-offers call. Only location/date changes
      // re-issue it, so the tiles hold steady while the table is filtered.
      case FETCH_OFFERS_KPI_REQUEST:
        draft.offersKpiLoading = true;
        draft.offersKpiTopOffer = null;
        draft.offersKpiActiveOffers = 0;
        draft.offersKpiLoaded = false;
        draft.offersKpiFailure = false;
        break;
      case FETCH_OFFERS_KPI_SUCCESS:
        draft.offersKpiLoading = false;
        draft.offersKpiTopOffer = action.payload.content?.[0] ?? null;
        draft.offersKpiActiveOffers = Number(action.payload.activeOffers) || 0;
        draft.offersKpiLoaded = true;
        draft.offersKpiFailure = false;
        break;
      case FETCH_OFFERS_KPI_FAILURE:
        draft.offersKpiLoading = false;
        draft.offersKpiTopOffer = null;
        draft.offersKpiActiveOffers = 0;
        draft.offersKpiLoaded = false;
        draft.offersKpiFailure = true;
        break;

      case FETCH_OFFER_REDEMPTION_TREND_REQUEST:
        draft.redemptionTrendLoading = true;
        draft.redemptionTrend = null;
        draft.redemptionTrendFailure = false;
        break;
      case FETCH_OFFER_REDEMPTION_TREND_SUCCESS:
        draft.redemptionTrendLoading = false;
        draft.redemptionTrend = action.payload;
        draft.redemptionTrendFailure = false;
        break;
      case FETCH_OFFER_REDEMPTION_TREND_FAILURE:
        draft.redemptionTrendLoading = false;
        draft.redemptionTrend = null;
        draft.redemptionTrendFailure = true;
        break;

      case FETCH_OFFER_DISCOUNT_BREAKDOWN_REQUEST:
        draft.discountBreakdownLoading = true;
        draft.discountBreakdown = null;
        draft.discountBreakdownFailure = false;
        break;
      case FETCH_OFFER_DISCOUNT_BREAKDOWN_SUCCESS:
        draft.discountBreakdownLoading = false;
        draft.discountBreakdown = action.payload;
        draft.discountBreakdownFailure = false;
        break;
      case FETCH_OFFER_DISCOUNT_BREAKDOWN_FAILURE:
        draft.discountBreakdownLoading = false;
        draft.discountBreakdown = null;
        draft.discountBreakdownFailure = true;
        break;

      case FETCH_OFFER_DISCOUNT_BY_TYPE_REQUEST:
        draft.discountByTypeLoading = true;
        draft.discountByType = null;
        draft.discountByTypeFailure = false;
        break;
      case FETCH_OFFER_DISCOUNT_BY_TYPE_SUCCESS:
        draft.discountByTypeLoading = false;
        draft.discountByType = action.payload;
        draft.discountByTypeFailure = false;
        break;
      case FETCH_OFFER_DISCOUNT_BY_TYPE_FAILURE:
        draft.discountByTypeLoading = false;
        draft.discountByType = null;
        draft.discountByTypeFailure = true;
        break;

      case FETCH_OFFER_DETAIL_REQUEST:
        draft.offerDetailLoading = true;
        draft.offerDetail = null;
        draft.offerDetailFailure = false;
        break;
      case FETCH_OFFER_DETAIL_SUCCESS:
        draft.offerDetailLoading = false;
        draft.offerDetail = action.payload;
        draft.offerDetailFailure = false;
        break;
      case FETCH_OFFER_DETAIL_FAILURE:
        draft.offerDetailLoading = false;
        draft.offerDetail = null;
        draft.offerDetailFailure = true;
        break;
      case CLEAR_OFFER_DETAIL:
        draft.offerDetailLoading = false;
        draft.offerDetail = null;
        draft.offerDetailFailure = false;
        break;

      default:
        return state;
    }
  });
}
